// Funnel analytics. GA4 fires via the GTM container (loaded on consent Accept),
// so the app only pushes to dataLayer for GA4. Meta Pixel (fbq) and TikTok (ttq)
// load after consent (Accept), so those calls are no-ops until then.
function push(event, payload = {}) {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...payload });
  } catch (e) {
    /* no-op */
  }
}

// Safe TikTok Pixel forwarder. ttq only exists after consent (Accept).
function ttqTrack(event, payload = {}) {
  try {
    if (typeof window.ttq?.track === "function") window.ttq.track(event, payload);
  } catch (e) {
    /* no-op */
  }
}

// Generic custom event (dataLayer + Meta). "page_view" maps to the ViewContent
// standard event on Meta + TikTok; all other events stay custom.
export function trackEvent(event, payload = {}) {
  push(event, payload);
  try {
    if (event === "page_view") {
      if (typeof window.fbq === "function") window.fbq("track", "ViewContent", payload);
      ttqTrack("ViewContent", payload);
    } else if (typeof window.fbq === "function") {
      window.fbq("trackCustom", event, payload);
    }
  } catch (e) {
    /* no-op */
  }
}

// Fire ONLY after the backend confirms a successful free-lead signup.
export function trackLead(payload = {}) {
  push("generate_lead", payload);
  try {
    if (typeof window.fbq === "function") window.fbq("track", "Lead", payload);
    ttqTrack("SubmitForm", payload);
  } catch (e) {
    /* no-op */
  }
}

// Fire ONLY after a valid checkout session/order has been created.
export function trackBeginCheckout({ value = 17, currency = "USD", method } = {}) {
  push("begin_checkout", { value, currency, method });
  try {
    if (typeof window.fbq === "function") window.fbq("track", "InitiateCheckout", { value, currency });
    ttqTrack("InitiateCheckout", { value, currency });
  } catch (e) {
    /* no-op */
  }
}

// Fire on the server-confirmed purchase (Thank You page after status check).
// transaction_id is also used as the Meta Pixel eventID so it de-dupes with the
// server-side Conversions API Purchase (same event_id on the backend).
export function trackPurchase({ value = 17, currency = "USD", transaction_id } = {}) {
  // Dedupe: fire the purchase event at most once per transaction (survives reloads).
  if (transaction_id) {
    try {
      const k = "cc_purchase_" + transaction_id;
      if (localStorage.getItem(k)) return;
      localStorage.setItem(k, "1");
    } catch (e) {
      /* no-op */
    }
  }
  push("purchase", { value, currency, transaction_id });
  try {
    if (typeof window.fbq === "function") {
      window.fbq("track", "Purchase", { value, currency }, transaction_id ? { eventID: transaction_id } : undefined);
    }
    ttqTrack("CompletePayment", transaction_id ? { value, currency, event_id: transaction_id } : { value, currency });
  } catch (e) {
    /* no-op */
  }
}

// ---------------------------------------------------------------------------
// GA4 growth events (single generic helper). GA4 fires ONLY through the GTM
// container (loaded on consent Accept), so the app just pushes to dataLayer.
// No direct gtag calls, to avoid double counting.
// ---------------------------------------------------------------------------
export function track(event, params = {}) {
  push(event, params);
}

const _once = new Set();
export function trackOnce(key, event, params = {}) {
  if (_once.has(key)) return;
  _once.add(key);
  track(event, params);
}

// Fire exactly once per page view.
export function trackHomepageView() {
  trackOnce("view_homepage", "view_homepage");
}
export function trackPricingView() {
  trackOnce("view_pricing", "view_pricing");
}

// Delegated primary-CTA tracking. Any element (or ancestor) with
// data-cta-location fires click_primary_cta{location}. Phase 2 homepage reuses
// this with zero rework by keeping the same attribute.
let _ctaInit = false;
export function initCtaTracking() {
  if (_ctaInit || typeof document === "undefined") return;
  _ctaInit = true;
  document.addEventListener(
    "click",
    (e) => {
      const el = e.target && e.target.closest ? e.target.closest("[data-cta-location]") : null;
      if (!el) return;
      track("click_primary_cta", { location: el.getAttribute("data-cta-location") });
    },
    true
  );
}

// Low Battery Mode events (defined now, fired in Phase 3).
export function trackResetStart(params = {}) {
  track("start_reset_mode", params);
}
export function trackResetComplete(params = {}) {
  track("complete_reset_action", params);
}

// Phase 4 free tool ("What should I do next?") events.
export function trackFreeTool(params = {}) {
  track("use_free_tool", params);
}
export function trackFreeToolToProduct(params = {}) {
  track("free_tool_to_product_click", params);
}
